import { TelemetrySink } from '@microsoft/oteljs'
import { Audience, IUserContext } from '@notes/aria-common-schema-logger'
import { Args, IAuthenticatedUserInfo, IErrorCodeInfo, ILogger, LogMessageFormatter, PrimitiveProperties } from '@notes/webintegrations-common'
import { Constants } from '../Constants'
import { GlobalVariables } from '../GlobalVariables'
import { getDeviceID } from '../uuid/getDeviceID'
import { EventName } from './EventName'
import { getOTelLoggingUserContextFromUserInformation } from './getLoggingUserContextFromUserInformation'
import { NotesIntegrationCommonLogger } from './NotesIntegrationCommonLogger'

export class CommonLogger implements ILogger {
  private namespace: string
  private commonLogger: NotesIntegrationCommonLogger

  constructor(namespace: string, sink?: TelemetrySink | undefined) {
    this.namespace = namespace

    const userInfo: IUserContext | undefined = undefined
    this.commonLogger = new NotesIntegrationCommonLogger({
      appName: Constants.stickyNotesOndcAppIdForLogging,
      appVersion: GlobalVariables.environmentSettings.version,
      audience: Audience.Dogfood, // TODO: specify correct audience
      deviceID: getDeviceID(),
      namespace: this.namespace,
    }, userInfo, sink)
  }

    /**
     * Logs an error.
     */
  error(message: string, ...args: Args): void {
    const formattedMessage = LogMessageFormatter.format(message, args)

    this.commonLogger.logONDCReportData('DiagnosticError', {
      Namespace: this.namespace,
      ShortEventName: 'DiagnosticError',
      Context: { Message: formattedMessage },
    })
  }

  /**
   * Logs information.
   */
  info(message: string, ...args: Args): void {
    const formattedMessage = LogMessageFormatter.format(message, args)

    this.commonLogger.logONDCReportData('DiagnosticInfo', {
      Namespace: this.namespace,
      ShortEventName: 'DiagnosticInfo',
      Context: { Message: formattedMessage },
    })
  }

  /**
   * Logs a warning.
   */
  warn(message: string, ...args: Args): void {
    const formattedMessage = LogMessageFormatter.format(message, args)

    this.commonLogger.logONDCReportData('DiagnosticWarn', {
      Namespace: this.namespace,
      ShortEventName: 'DiagnosticWarn',
      Context: { Message: formattedMessage },
    })
  }

    /**
     * Implements OneNote Common Schema ReportData logging.
     * Logs to Aria using the shared Sticky Notes tenant
     * DO NOT INCLUDE PII !!!
     */
  logReportData(eventName: EventName, _isCriticalEvent: boolean, payloadNonPII?: PrimitiveProperties, _payloadEUPIOnlyNonPII?: PrimitiveProperties): void {
    this.commonLogger.logONDCReportData(eventName, {
      Namespace: this.namespace,
      ShortEventName: eventName,
      Context: payloadNonPII,
    })
  }

  /**
   * Logs a unique event with a message.
   */
  logEvent(eventName: EventName, message: string, ...args: Args): void {
    const formattedMessage = LogMessageFormatter.format(message, args)

    this.commonLogger.logONDCReportData(eventName, {
      Namespace: this.namespace,
      ShortEventName: eventName,
      Context: { Message: formattedMessage },
    })
  }

  logUnexpected = (errorInfo: IErrorCodeInfo, errorDescription: string, sessionCorrelationId?: string) => {
    this.commonLogger.logONDCUnexpected({
      Namespace: this.namespace,
      ShortEventName: errorInfo.name,
      ErrorCode1: errorInfo.errorCode1,
      ErrorCode2: errorInfo.errorCode2,
      ErrorTag: errorInfo.errorTag,
      ErrorType: errorDescription,
      SessionCorrelationId: sessionCorrelationId,
    })
  }

  setUserContext(userInfo: IAuthenticatedUserInfo): void {
    // if we are using a new middleware for getting user context info
    const otelUserContextForLogging = getOTelLoggingUserContextFromUserInformation(userInfo)
    if (otelUserContextForLogging) {
      this.commonLogger.setUserContext(otelUserContextForLogging)
    }
  }

}
